'use client'

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link } from '@/shared/navigation'
import { fetchTodayAttendance } from '@/features/kitchen/api/getTodayAttendance'
import { shouldUseKitchenApi } from '@/features/kitchen/api/kitchenApiMode'
import {
  mapAttendanceRecordsFromApi,
  mapStaffFromApi,
} from '@/features/kitchen/api/mapAttendanceFromApi'
import { submitAttendance, type AttendanceAction } from '@/features/kitchen/api/submitAttendance'
import { uploadPhoto } from '@/shared/api/uploadPhoto'
import {
  getStaffRoleMeta,
  KITCHEN_STAFF_ROSTER,
  type KitchenStaffMember,
} from '@/features/kitchen/kitchenStaff'
import { kitchenTerms } from '@/features/kitchen/kitchenTerms'
import { FlowStepper } from '@/shared/components/FlowStepper'
import { PinKeypadModal } from '@/shared/components/PinKeypadModal'
import { SkeletonList } from '@/shared/components/Skeleton'
import { SelfieCapture } from '@/shared/components/SelfieCapture'
import { Toast } from '@/shared/components/Toast'
import {
  loadAttendanceDayRecords,
  saveAttendanceDayRecords,
  todayDateKey,
  type AttendanceEventRecord,
  type StaffDayAttendanceRecord,
} from '@/features/kitchen/storage/attendanceStorage'
import { getKitchenDevice } from '@/shared/auth/deviceStorage'
import { consumeLastSyncQueued } from '@/shared/sync/kitchenSync'

type AttendanceEvent = AttendanceEventRecord
type StaffDayAttendance = StaffDayAttendanceRecord

type FlowStep = 'home' | 'selfie' | 'confirm'

type StaffStatus = 'belum' | 'masuk' | 'selesai'

const TEAM_SHIFT_START = '06:00'

function formatTodayLabel(): string {
  return new Intl.DateTimeFormat('id-ID', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(new Date())
}

function formatTimeLabel(iso: string): string {
  return new Intl.DateTimeFormat('id-ID', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso))
}

function formatNowClock(): string {
  return new Intl.DateTimeFormat('id-ID', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date())
}

function getStaffStatus(record: StaffDayAttendance | undefined): StaffStatus {
  if (!record?.checkIn) return 'belum'
  if (!record.checkOut) return 'masuk'
  return 'selesai'
}

function statusLabel(status: StaffStatus): string {
  if (status === 'belum') return kitchenTerms.attendance.notYet
  if (status === 'masuk') return kitchenTerms.attendance.onShift
  return kitchenTerms.attendance.finished
}

function statusBadgeClass(status: StaffStatus): string {
  if (status === 'belum') return 'badge-draft'
  if (status === 'masuk') return 'badge-warning'
  return 'badge-success'
}

function isOnTimeCheckIn(iso: string, shiftStart: string): boolean {
  const event = new Date(iso)
  const start = new Date(event)
  const [hour, minute] = shiftStart.split(':').map(Number)
  start.setHours(hour, minute, 0, 0)
  return event.getTime() <= start.getTime()
}

function isPastTeamShiftStart(now: Date): boolean {
  const start = new Date(now)
  const [hour, minute] = TEAM_SHIFT_START.split(':').map(Number)
  start.setHours(hour, minute, 0, 0)
  return now.getTime() > start.getTime()
}

function formatDuration(checkIn: string, checkOut: string): string {
  const ms = new Date(checkOut).getTime() - new Date(checkIn).getTime()
  const totalMinutes = Math.max(0, Math.floor(ms / 60000))
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return `${hours}j ${minutes}m`
}

function revokePhotoUrl(url: string | null) {
  if (url?.startsWith('blob:')) {
    URL.revokeObjectURL(url)
  }
}

function StaffRoleChip({ staff }: { staff: KitchenStaffMember }) {
  const meta = getStaffRoleMeta(staff)
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-label-md font-semibold uppercase tracking-wide text-[10px] px-2 py-0.5 ${meta.chipClass}`}
    >
      <span className="material-symbols-outlined text-sm leading-none">{meta.icon}</span>
      {staff.roleLabel}
    </span>
  )
}

const ATTENDANCE_FLOW_STEPS = [
  { label: 'Pilih' },
  { label: 'Selfie' },
  { label: 'Konfirmasi' },
  { label: 'PIN' },
] as const

function AttendanceFlowStepper({
  current,
  pinOpen,
}: {
  current: 1 | 2 | 3
  pinOpen: boolean
}) {
  return <FlowStepper steps={[...ATTENDANCE_FLOW_STEPS]} activeStep={pinOpen ? 4 : current} />
}

function AttendanceSelfieThumb({ src, alt }: { src: string | null | undefined; alt: string }) {
  if (!src) {
    return (
      <div className="w-full h-full bg-surface-variant flex items-center justify-center">
        <span className="material-symbols-outlined text-on-surface-variant text-lg" aria-hidden>
          person
        </span>
      </div>
    )
  }
  return <img src={src} alt={alt} className="w-full h-full object-cover" />
}

function StaffAttendanceCard({
  staff,
  record,
  onAction,
  muted = false,
}: {
  staff: KitchenStaffMember
  record: StaffDayAttendance | undefined
  onAction: (staff: KitchenStaffMember) => void
  muted?: boolean
}) {
  const status = getStaffStatus(record)
  const canAct = status !== 'selesai'

  return (
    <div className={`card space-y-3 ${muted ? 'opacity-75' : ''}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0 space-y-2">
          <p className="text-body-lg font-bold text-on-surface">{staff.name}</p>
          <div className="flex flex-wrap items-center gap-2">
            <StaffRoleChip staff={staff} />
            <span className="text-label-md text-on-surface-variant">
              {staff.shiftStart} – {staff.shiftEnd}
            </span>
          </div>
        </div>
        <span className={statusBadgeClass(status)}>{statusLabel(status)}</span>
      </div>

      {status === 'masuk' && record?.checkIn && (
        <div className="flex items-center gap-3 rounded-lg bg-surface-container p-3">
          <div className="w-11 h-11 rounded-lg overflow-hidden border border-outline-variant shrink-0">
            <AttendanceSelfieThumb src={record.checkIn.photoUrl} alt={`Selfie ${staff.name}`} />
          </div>
          <div className="text-body-sm">
            <p className="text-on-surface-variant">{kitchenTerms.attendance.checkIn}</p>
            <p className="font-mono font-bold text-body-lg">{record.checkIn.timeLabel}</p>
            <p
              className={`text-label-md font-semibold ${
                record.checkIn.onTime ? 'text-status-success' : 'text-status-warning'
              }`}
            >
              {record.checkIn.onTime ? 'Tepat waktu' : 'Terlambat'}
            </p>
          </div>
        </div>
      )}

      {status === 'selesai' && record?.checkIn && record.checkOut && (
        <div className="rounded-lg bg-surface-container p-3 text-body-sm space-y-2">
          <div className="flex gap-2">
            <div className="w-10 h-10 rounded-lg overflow-hidden border border-outline-variant">
              <AttendanceSelfieThumb src={record.checkIn.photoUrl} alt="Masuk" />
            </div>
            <div className="w-10 h-10 rounded-lg overflow-hidden border border-outline-variant">
              <AttendanceSelfieThumb src={record.checkOut.photoUrl} alt="Keluar" />
            </div>
          </div>
          <p>
            <span className="text-on-surface-variant">Masuk</span>{' '}
            <strong className="font-mono">{record.checkIn.timeLabel}</strong>
            <span className="mx-2">·</span>
            <span className="text-on-surface-variant">Keluar</span>{' '}
            <strong className="font-mono">{record.checkOut.timeLabel}</strong>
          </p>
          <p className="text-on-surface-variant">
            Durasi:{' '}
            <strong className="text-on-surface">
              {formatDuration(record.checkIn.at, record.checkOut.at)}
            </strong>
          </p>
        </div>
      )}

      {canAct && (
        <button
          type="button"
          className={`w-full min-h-[44px] rounded-lg font-bold px-4 py-2 ${
            status === 'belum'
              ? 'btn-primary'
              : 'border-2 border-status-warning text-status-warning bg-status-warning-bg/30'
          }`}
          onClick={() => onAction(staff)}
        >
          {status === 'belum'
            ? kitchenTerms.attendance.checkIn
            : `${kitchenTerms.attendance.checkOut} · selfie + PIN`}
        </button>
      )}
    </div>
  )
}

function StaffGroup({
  title,
  children,
  defaultOpen = true,
}: {
  title: string
  children: ReactNode
  defaultOpen?: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="space-y-2">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 px-1 min-h-[44px]"
        onClick={() => setOpen((prev) => !prev)}
      >
        <p className="text-body-sm font-semibold text-on-surface">{title}</p>
        <span className="material-symbols-outlined text-on-surface-variant">
          {open ? 'expand_less' : 'expand_more'}
        </span>
      </button>
      {open && <div className="space-y-2">{children}</div>}
    </div>
  )
}

export function KitchenAttendancePage() {
  const useApi = shouldUseKitchenApi()
  const device = getKitchenDevice()
  const date = todayDateKey()

  const [roster, setRoster] = useState<KitchenStaffMember[]>(() =>
    useApi ? [] : KITCHEN_STAFF_ROSTER,
  )
  const [records, setRecords] = useState<Record<string, StaffDayAttendance>>(() =>
    useApi ? {} : loadAttendanceDayRecords(date),
  )
  const [isLoading, setIsLoading] = useState(useApi)
  const [step, setStep] = useState<FlowStep>('home')
  const [selectedStaff, setSelectedStaff] = useState<KitchenStaffMember | null>(null)
  const [pendingAction, setPendingAction] = useState<AttendanceAction | null>(null)
  const [selfiePreview, setSelfiePreview] = useState<string | null>(null)
  const [selfieError, setSelfieError] = useState<string | null>(null)
  const [isPinOpen, setIsPinOpen] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const [nowClock, setNowClock] = useState(formatNowClock)

  useEffect(() => {
    if (!useApi || !device?.deviceId) {
      setIsLoading(false)
      return
    }

    let cancelled = false
    setIsLoading(true)
    fetchTodayAttendance(device.deviceId)
      .then((data) => {
        if (cancelled) return
        setRoster(mapStaffFromApi(data.staff))
        setRecords(mapAttendanceRecordsFromApi(data.staff, date))
      })
      .catch(() => {
        if (!cancelled) {
          setRoster([])
          setRecords({})
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [useApi, device?.deviceId, date])

  const kitchenName = device?.kitchenName ?? 'Dapur'
  const todayLabel = useMemo(() => formatTodayLabel(), [])

  useEffect(() => {
    const timer = window.setInterval(() => setNowClock(formatNowClock()), 30_000)
    return () => window.clearInterval(timer)
  }, [])

  const dismissToast = useCallback(() => setToast(null), [])
  const showToast = useCallback((message: string) => setToast(message), [])

  const staffGroups = useMemo(() => {
    const belum: KitchenStaffMember[] = []
    const masuk: KitchenStaffMember[] = []
    const selesai: KitchenStaffMember[] = []

    for (const staff of roster) {
      const status = getStaffStatus(records[staff.id])
      if (status === 'belum') belum.push(staff)
      else if (status === 'masuk') masuk.push(staff)
      else selesai.push(staff)
    }

    return { belum, masuk, selesai }
  }, [records, roster])

  const checkedInCount = roster.filter((member) => records[member.id]?.checkIn).length
  const finishedCount = staffGroups.selesai.length
  const progressPct = roster.length > 0 ? Math.round((checkedInCount / roster.length) * 100) : 0
  const missingNames = staffGroups.belum.map((s) => s.name.split(' ')[0]).join(', ')
  const showMissingAlert = isPastTeamShiftStart(new Date()) && staffGroups.belum.length > 0

  const flowStepNumber: 1 | 2 | 3 =
    step === 'home' ? 1 : step === 'selfie' ? 2 : 3

  function resetFlow() {
    revokePhotoUrl(selfiePreview)
    setSelfiePreview(null)
    setSelfieError(null)
    setPendingAction(null)
    setSelectedStaff(null)
    setIsPinOpen(false)
    setStep('home')
  }

  function startAction(staff: KitchenStaffMember, action: AttendanceAction) {
    setSelectedStaff(staff)
    setPendingAction(action)
    setSelfieError(null)
    setSelfiePreview(null)
    setStep('selfie')
  }

  function handleStaffAction(staff: KitchenStaffMember) {
    const status = getStaffStatus(records[staff.id])
    if (status === 'belum') startAction(staff, 'check_in')
    else if (status === 'masuk') startAction(staff, 'check_out')
  }

  function handlePhotoChange(url: string | null) {
    if (selfiePreview && url !== selfiePreview) {
      revokePhotoUrl(selfiePreview)
    }
    setSelfiePreview(url)
    setSelfieError(null)
  }

  function openConfirmStep() {
    if (!selfiePreview) {
      setSelfieError('Ambil selfie terlebih dahulu.')
      return
    }
    setStep('confirm')
  }

  async function confirmAttendance(pin: string) {
    if (!device || !pendingAction || !selfiePreview || !selectedStaff) {
      throw new Error('Data absensi tidak lengkap.')
    }

    const clientEventId = crypto.randomUUID()
    const uploaded = await uploadPhoto(selfiePreview, 'attendance', { clientEventId })

    const result = await submitAttendance({
      deviceId: device.deviceId,
      userId: selectedStaff.id,
      pin,
      action: pendingAction,
      photoUrl: uploaded.key,
    })

    const event: AttendanceEvent = {
      at: result.recorded_at,
      timeLabel: formatTimeLabel(result.recorded_at),
      photoUrl: uploaded.url,
      onTime:
        pendingAction === 'check_in'
          ? (result.on_time ?? isOnTimeCheckIn(result.recorded_at, selectedStaff.shiftStart))
          : undefined,
    }

    const existing = records[selectedStaff.id]
    const nextRecord: StaffDayAttendance = {
      userId: selectedStaff.id,
      date,
      checkIn: existing?.checkIn,
      checkOut: existing?.checkOut,
    }

    if (pendingAction === 'check_in') nextRecord.checkIn = event
    else nextRecord.checkOut = event

    const nextRecords = { ...records, [selectedStaff.id]: nextRecord }
    if (!useApi) {
      saveAttendanceDayRecords(date, nextRecords)
    }
    setRecords(nextRecords)
    setIsPinOpen(false)

    const staffName = selectedStaff.name
    const verb =
      pendingAction === 'check_in'
        ? kitchenTerms.attendance.checkInVerb
        : kitchenTerms.attendance.checkOutVerb
    let message =
      pendingAction === 'check_in'
        ? `${staffName} ${verb} ${event.timeLabel}${event.onTime ? ' · Tepat waktu' : ' · Terlambat'}`
        : `${staffName} ${verb} ${event.timeLabel}`

    if (consumeLastSyncQueued()) {
      message += ` · ${kitchenTerms.sync.queued}`
    }

    resetFlow()
    showToast(message)
  }

  const actionLabel =
    pendingAction === 'check_out'
      ? kitchenTerms.attendance.checkOutVerb
      : kitchenTerms.attendance.checkInVerb
  const actionTitle =
    pendingAction === 'check_out' ? kitchenTerms.attendance.checkOut : kitchenTerms.attendance.checkIn

  return (
    <section className="space-y-4">
      {step === 'home' && (
        <>
          {isLoading && <SkeletonList count={3} label="Memuat daftar tim dapur" />}
          <div className="card space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-full bg-primary-container text-on-primary-container flex items-center justify-center shrink-0">
                <span className="material-symbols-outlined text-2xl">groups</span>
              </div>
              <div className="flex-1">
                <p className="text-headline-md text-primary">{kitchenTerms.modules.attendance} tim dapur</p>
                <p className="text-body-sm text-on-surface-variant capitalize">
                  {kitchenName} · {todayLabel}
                </p>
                <p className="text-label-md text-on-surface-variant mt-1">
                  Sekarang <strong className="font-mono text-on-surface">{nowClock}</strong> · Shift mulai{' '}
                  {TEAM_SHIFT_START}
                </p>
              </div>
            </div>

            <p className="text-body-sm text-on-surface-variant bg-surface-container rounded-lg px-3 py-2 border border-outline-variant">
              Tablet dipakai bergantian — tiap orang pilih nama sendiri, selfie, lalu PIN pribadi.
            </p>

            <div className="space-y-2">
              <div className="flex justify-between text-body-sm">
                <span className="text-on-surface-variant">{kitchenTerms.attendance.checkIn} tim</span>
                <span className="font-semibold">
                  {checkedInCount}/{roster.length} orang
                </span>
              </div>
              <div className="h-2 rounded-full bg-surface-container overflow-hidden">
                <div
                  className="h-full bg-status-success transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <p className="text-label-md text-on-surface-variant">
                Selesai shift: <strong>{finishedCount}</strong> orang
                {staffGroups.belum.length > 0 && (
                  <>
                    {' '}
                    · Belum masuk: <strong>{missingNames}</strong>
                  </>
                )}
              </p>
            </div>

            {showMissingAlert && (
              <div className="rounded-lg border border-status-warning/40 bg-status-warning-bg/50 px-3 py-2 text-body-sm text-status-warning">
                <strong>{staffGroups.belum.length} orang belum {kitchenTerms.attendance.checkInVerb}</strong>{' '}
                padahal shift sudah dimulai.
              </div>
            )}
          </div>

          {staffGroups.belum.length > 0 && (
            <StaffGroup title={`Perlu ${kitchenTerms.attendance.checkInVerb} (${staffGroups.belum.length})`}>
              {staffGroups.belum.map((staff) => (
                <StaffAttendanceCard
                  key={staff.id}
                  staff={staff}
                  record={records[staff.id]}
                  onAction={handleStaffAction}
                />
              ))}
            </StaffGroup>
          )}

          {staffGroups.masuk.length > 0 && (
            <StaffGroup title={`Sedang shift (${staffGroups.masuk.length})`}>
              {staffGroups.masuk.map((staff) => (
                <StaffAttendanceCard
                  key={staff.id}
                  staff={staff}
                  record={records[staff.id]}
                  onAction={handleStaffAction}
                />
              ))}
            </StaffGroup>
          )}

          {staffGroups.selesai.length > 0 && (
            <StaffGroup title={`Selesai (${staffGroups.selesai.length})`} defaultOpen={false}>
              {staffGroups.selesai.map((staff) => (
                <StaffAttendanceCard
                  key={staff.id}
                  staff={staff}
                  record={records[staff.id]}
                  onAction={handleStaffAction}
                  muted
                />
              ))}
            </StaffGroup>
          )}

          <Link href="/kitchen/home" className="btn-outline block text-center min-h-[44px]">
            Kembali ke beranda
          </Link>
        </>
      )}

      {step !== 'home' && selectedStaff && pendingAction && (
        <div className="card space-y-3">
          <AttendanceFlowStepper current={flowStepNumber} pinOpen={isPinOpen} />

          <div className="flex items-center justify-between gap-2 border-b border-surface-variant pb-3">
            <div className="space-y-1">
              <p className="text-headline-md text-primary">{actionTitle}</p>
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-body-sm text-on-surface-variant">{selectedStaff.name}</p>
                <StaffRoleChip staff={selectedStaff} />
              </div>
            </div>
            <button type="button" className="btn-outline min-h-[44px]" onClick={resetFlow}>
              Batal
            </button>
          </div>
        </div>
      )}

      {step === 'selfie' && pendingAction && selectedStaff && (
        <div className="card space-y-3">
          <SelfieCapture
            photoUrl={selfiePreview}
            onPhotoChange={handlePhotoChange}
            staffName={selectedStaff.name}
          />

          {selfieError && (
            <div role="alert" className="alert-error">
              <span className="material-symbols-outlined text-base shrink-0">error</span>
              <span>{selfieError}</span>
            </div>
          )}

          <button
            type="button"
            className="btn-primary w-full min-h-[44px] disabled:opacity-50"
            disabled={!selfiePreview}
            onClick={openConfirmStep}
          >
            Lanjut konfirmasi
          </button>
        </div>
      )}

      {step === 'confirm' && pendingAction && selectedStaff && selfiePreview && (
        <div className="card space-y-3">
          <p className="text-body-sm text-on-surface-variant text-center">
            Pastikan nama dan foto sudah benar sebelum PIN
          </p>

          <div className="rounded-xl border border-outline-variant overflow-hidden">
            <img
              src={selfiePreview}
              alt={`Konfirmasi ${selectedStaff.name}`}
              className="w-full aspect-[4/3] object-cover"
            />
          </div>

          <div className="rounded-lg bg-surface-container p-3 space-y-2 text-body-sm">
            <div className="flex justify-between gap-2">
              <span className="text-on-surface-variant">Nama</span>
              <strong className="text-on-surface">{selectedStaff.name}</strong>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-on-surface-variant">Peran</span>
              <span>{selectedStaff.roleLabel}</span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-on-surface-variant">Aksi</span>
              <strong className="capitalize">{actionLabel}</strong>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-on-surface-variant">Waktu</span>
              <strong className="font-mono">{nowClock}</strong>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <button type="button" className="btn-primary w-full min-h-[44px]" onClick={() => setIsPinOpen(true)}>
              Lanjut ke PIN
            </button>
            <button type="button" className="btn-outline w-full min-h-[44px]" onClick={() => setStep('selfie')}>
              Ulangi selfie
            </button>
          </div>

          <p className="text-label-md text-on-surface-variant text-center">
            Gunakan PIN pribadi {selectedStaff.name}.
          </p>
        </div>
      )}

      <PinKeypadModal
        open={isPinOpen}
        title={`Konfirmasi ${actionLabel}`}
        description={`PIN ${selectedStaff?.name ?? 'staff'} untuk ${actionLabel}.`}
        confirmLabel="Submit absensi"
        onClose={() => setIsPinOpen(false)}
        onConfirm={confirmAttendance}
      />

      {toast && <Toast message={toast} onDismiss={dismissToast} />}
    </section>
  )
}
